import { Address } from '@mwe/models';
import { Adresse } from '../../../../generated/model/adresse.model';
import { Kunde } from '../../../../generated/model/kunde.model';
import { Unternehmen } from '../../../../generated/model/unternehmen.model';
import { Company } from '@shared-ikp/model/company.model';
import { MeterPoint } from '../../../model/meter-point.model';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { isUnternehmen } from './beteiligte-partei.util';
import { MeterpointStatus } from '../../../model/meterpoint-status.model';
import { FALLBACK_VALUE } from './ummeldung.constants';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import {
  BisherigerVertragspartnerFormValue,
  BisherigerVertragspartnerStepFormValue,
  RechnungsempfaengerFormValue,
  VertragspartnerMode,
} from '../schemas';
import {
  initialNeuerRechnungsempfaengerFormValue,
  mapAbweichenderRechnungsempfaengerToUnternehmen,
  mapAdresseToFormValue,
  mapKundeToPrivatUnternehmenFormValue,
  mapKundeToUnterschriftFormValue,
  mapPrivatUnternehmenFormValueToKunde,
  withUnterschrift,
} from './common-vertragspartner.mapper';
import { UmmeldungStoreType } from '../models';

export function mapGeschaeftspartnerToUnternehmen(geschaeftspartner: Company): Unternehmen {
  return {
    name: geschaeftspartner.name,
    kundennummer: geschaeftspartner.gpNr,
    uid: geschaeftspartner.uid,
    adresse: mapAddressToAdresse(geschaeftspartner.address),
  };
}

export function mapRechnungsempfaengerToUnternehmen(zaehlpunkt: MeterPoint): Unternehmen {
  return {
    name: zaehlpunkt.rechnungsempfaengerName,
    kundennummer: zaehlpunkt.rechnungsempfaengerGpNr,
    uid: zaehlpunkt.rechnungsempfaengerUid,
    adresse: mapAddressToAdresse(zaehlpunkt.rechnungsAdresse),
  };
}

export function mapAddressToAdresse(address: Address | null | undefined): Adresse {
  return {
    ort: address?.city ?? '',
    plz: address?.postcode ?? '',
    strasse: address?.street ?? '',
    hausnummer: address?.streetNumber ?? '',
    stiege: address?.block ?? '',
    tuernummer: address?.doorNumber ?? '',
  };
}

export interface ContractPartnerReadonlyFields {
  name: string;
  kundennummer: string;
  adresse: string;
}

export interface InvoiceRecipientReadonlyFields extends ContractPartnerReadonlyFields {
  uid: string;
}

export function mapVertragspartnerToReadonlyFields(vertragspartner: Company | null): ContractPartnerReadonlyFields {
  return {
    name: vertragspartner?.name || FALLBACK_VALUE,
    kundennummer: vertragspartner?.gpNr || FALLBACK_VALUE,
    adresse: getAddressStreetAndPostCodeFormatted(vertragspartner?.address) || FALLBACK_VALUE,
  };
}

export function mapRechnungsempfaengerToReadonlyFields(zaehlpunkt: MeterPoint | null): InvoiceRecipientReadonlyFields {
  return {
    name: zaehlpunkt?.rechnungsempfaengerName || FALLBACK_VALUE,
    kundennummer: zaehlpunkt?.rechnungsempfaengerGpNr || FALLBACK_VALUE,
    adresse: getAddressStreetAndPostCodeFormatted(zaehlpunkt?.rechnungsAdresse) || FALLBACK_VALUE,
    uid: zaehlpunkt?.rechnungsempfaengerUid || FALLBACK_VALUE,
  };
}

export function mapBisherigerVertragspartnerToFormValue(bisherigerKunde: Kunde | null | undefined): BisherigerVertragspartnerFormValue {
  if (bisherigerKunde?.kundeTyp === Kunde.KundeTypEnum.UNBEKANNT) {
    return {
      vertragspartnerBekanntheit: 'unknown',
      privatUnternehmen: mapKundeToPrivatUnternehmenFormValue(bisherigerKunde),
      adresse: null,
    };
  }

  return {
    vertragspartnerBekanntheit: 'known',
    privatUnternehmen: mapKundeToPrivatUnternehmenFormValue(bisherigerKunde),
    adresse: mapAdresseToFormValue(bisherigerKunde?.vertragspartner?.adresse ?? bisherigerKunde?.rechnungsempfaenger?.adresse),
  };
}

export function mapRechnungsempfaengerToFormValue(rechnungsempfaenger: Unternehmen | null): RechnungsempfaengerFormValue {
  return {
    hasAbweichendenRechnungsempfaenger: rechnungsempfaenger !== null,
    name: rechnungsempfaenger?.name ?? '',
    kundennummer: rechnungsempfaenger?.kundennummer ?? '',
    uid: rechnungsempfaenger?.uid ?? '',
    firmenbuchnummer: rechnungsempfaenger?.firmenbuchnummer ?? '',
    adresse: {
      plz: rechnungsempfaenger?.adresse?.plz ?? '',
      ort: rechnungsempfaenger?.adresse?.ort ?? '',
      strasse: rechnungsempfaenger?.adresse?.strasse ?? '',
      hausnummer: getHausnummerStiege(rechnungsempfaenger?.adresse),
      stiege: '',
      tuernummer: rechnungsempfaenger?.adresse?.tuernummer ?? '',
    },
  };
}

function getHausnummerStiege(address: Adresse | null | undefined): string {
  return [address?.hausnummer, address?.stiege].filter(Boolean).join('/');
}

export function resolveBisherigerVertragspartnerMode(zaehlpunkt: MeterPoint | null | undefined): VertragspartnerMode {
  if (zaehlpunkt?.status !== MeterpointStatus.AKTIV) {
    return VertragspartnerMode.BEARBEITBAR;
  }

  return zaehlpunkt.hasAbweichenderRechnungsempfaenger ? VertragspartnerMode.VERWALTETER_KUNDE : VertragspartnerMode.ACCOUNT_KUNDE;
}

export interface BisherigerKundeBuildParams {
  mode: VertragspartnerMode;
  zaehlpunkt: MeterPoint | null;
  vertragspartnerUnternehmen: Company | null;
  bisherigerKunde: Kunde | null | undefined;
  rechnungsempfaengerDraft?: RechnungsempfaengerFormValue;
}

/**
 * The single place where `bisherigerKunde` is assembled from the resolved mode, the loaded
 * Geschäftspartner company, the Zählpunkt and step 3's Rechnungsempfänger draft. Called by step 3
 * when it syncs to the store and by the store when the Geschäftspartner company finishes loading
 * (so a save before step 3 is ever visited still persists a complete `bisherigerKunde`); nothing
 * else derives `kundeTyp`/`vertragspartner`/`rechnungsempfaenger`.
 *
 * Without `rechnungsempfaengerDraft` (the store's eager call has no step-3 draft) the existing
 * `rechnungsempfaenger` is preserved. The signature fields are owned by the step draft and put on
 * top by buildBisherigerKundeFromStepDraft; the eager call keeps them via spread.
 */
export function buildBisherigerKunde(bisherigerKundeBuildParams: BisherigerKundeBuildParams): Kunde {
  const { mode, zaehlpunkt, vertragspartnerUnternehmen, bisherigerKunde, rechnungsempfaengerDraft } = bisherigerKundeBuildParams;

  if (mode === VertragspartnerMode.BEARBEITBAR) {
    return { ...bisherigerKunde };
  }

  const vertragspartner = vertragspartnerUnternehmen
    ? mapGeschaeftspartnerToUnternehmen(vertragspartnerUnternehmen)
    : bisherigerKunde?.vertragspartner;

  if (mode === VertragspartnerMode.VERWALTETER_KUNDE) {
    return {
      ...bisherigerKunde,
      kundeTyp: Kunde.KundeTypEnum.VERWALTETER_KUNDE,
      vertragspartner,
      rechnungsempfaenger: zaehlpunkt ? mapRechnungsempfaengerToUnternehmen(zaehlpunkt) : (bisherigerKunde?.rechnungsempfaenger ?? null),
    };
  }

  return {
    ...bisherigerKunde,
    kundeTyp: Kunde.KundeTypEnum.ACCOUNT_KUNDE,
    vertragspartner,
    rechnungsempfaenger: rechnungsempfaengerDraft
      ? mapAbweichenderRechnungsempfaengerToUnternehmen(rechnungsempfaengerDraft)
      : (bisherigerKunde?.rechnungsempfaenger ?? null),
  };
}

export function getStoredRechnungsempfaenger(ummeldung: Ummeldung | null): Unternehmen | null {
  const rechnungsempfaenger = ummeldung?.bisherigerKunde?.rechnungsempfaenger;
  return isUnternehmen(rechnungsempfaenger) ? rechnungsempfaenger : null;
}

export function mapBisherigerVertragspartnerFormValueToKunde(formValue: BisherigerVertragspartnerFormValue): Kunde {
  if (formValue.vertragspartnerBekanntheit === 'unknown') {
    return {
      kundeTyp: Kunde.KundeTypEnum.UNBEKANNT,
      vertragspartner: null,
      email: null,
      telefon: null,
    };
  }

  return mapPrivatUnternehmenFormValueToKunde(formValue.privatUnternehmen, formValue.adresse);
}

export function mapBisherigerKundeToStepFormValue(
  ummeldung: Ummeldung | null,
  mode: VertragspartnerMode,
): BisherigerVertragspartnerStepFormValue {
  const bisherigerKunde = ummeldung?.bisherigerKunde;

  return {
    rechnungsempfaenger:
      mode === VertragspartnerMode.ACCOUNT_KUNDE
        ? mapRechnungsempfaengerToFormValue(getStoredRechnungsempfaenger(ummeldung))
        : initialNeuerRechnungsempfaengerFormValue(),
    vertragspartner: mapBisherigerVertragspartnerToFormValue(mode === VertragspartnerMode.BEARBEITBAR ? bisherigerKunde : null),
    ...mapKundeToUnterschriftFormValue(bisherigerKunde),
  };
}

export function buildBisherigerKundeFromStepDraft(draft: BisherigerVertragspartnerStepFormValue, store: UmmeldungStoreType): Kunde {
  const mode = store.bisherigerVertragspartnerMode();
  const bisherigerKunde = buildBisherigerKunde({
    mode,
    zaehlpunkt: store.aktuellerZaehlpunkt(),
    vertragspartnerUnternehmen: store.zaehlpunktUnternehmen(),
    bisherigerKunde:
      mode === VertragspartnerMode.BEARBEITBAR
        ? mapBisherigerVertragspartnerFormValueToKunde(draft.vertragspartner)
        : store.activeUmmeldung()?.bisherigerKunde,
    rechnungsempfaengerDraft: draft.rechnungsempfaenger,
  });

  return withUnterschrift(bisherigerKunde, draft);
}
